import { useEffect, useState } from "react";
import "../styles/Contents.css";

export default function Results() {
  // Animated counter states
  const [passRate, setPassRate] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [avgProgress, setAvgProgress] = useState(0);

  // Intersection Observer for reveal animations
  useEffect(() => {
    const elements = document.querySelectorAll(
      ".reveal, .reveal-left, .reveal-right"
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          entry.isIntersecting
            ? entry.target.classList.add("active")
            : entry.target.classList.remove("active")
        );
      },
      { threshold: 0.15 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Animate counters
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const intervalTime = 30;

    const increment = (target, setter) => {
      let current = 0;
      const step = Math.ceil(target / (duration / intervalTime));
      const interval = setInterval(() => {
        current += step;
        if (current >= target) {
          setter(target);
          clearInterval(interval);
        } else {
          setter(current);
        }
      }, intervalTime);
    };

    increment(100, setPassRate); // 100% pass rate
    increment(95, setHighScore); // 95%+ top score
    increment(85, setAvgProgress); // 85%+ majority scoring
  }, []);

  return (
    <div className="page">
      {/* HERO */}
      <section className="about-hero reveal">
        <h1>Our Results</h1>
        <p>
          Proven academic excellence achieved through strong fundamentals,
          disciplined preparation, regular assessment, and dedicated mentoring.
        </p>
      </section>

      {/* PERFORMANCE OVERVIEW */}
      <section className="about-section reveal-left">
        <h2>📊 Academic Performance Overview</h2>
        <p>
          ACCESS has consistently delivered strong academic outcomes across
          classes by focusing on concept clarity, practice, and continuous
          evaluation.
        </p>

        <ul className="feature-list">
          <li>High pass percentage across all classes</li>
          <li>Steady year-on-year academic improvement</li>
          <li>Significant progress in average & weak students</li>
          <li>Regular internal tests and performance analysis</li>
          <li>Focus on accuracy, speed, and exam confidence</li>
        </ul>

        {/* Animated Counters */}
        <div className="counters">
          <div className="counter-card">
            <h3>{passRate}%</h3>
            <p>Pass Rate</p>
          </div>
          <div className="counter-card">
            <h3>{highScore}%+</h3>
            <p>Top Scores</p>
          </div>
          <div className="counter-card">
            <h3>{avgProgress}%+</h3>
            <p>Majority Scoring</p>
          </div>
        </div>
      </section>

      {/* CLASS 10 RESULTS */}
      <section className="about-section reveal-right">
        <h2>🏆 Class 10 Board Achievements</h2>
        <p>
          Our Class 10 students consistently achieve outstanding results across
          State Board and CBSE examinations.
        </p>

        <ul className="feature-list">
          <li>100% pass rate in recent board exams</li>
          <li>Multiple students scoring above 90%</li>
          <li>Strong performance in Mathematics & Science</li>
          <li>Improved answer presentation and time management</li>
        </ul>
      </section>

      {/* TOPPERS */}
      <section className="toppers-section reveal">
        <h2>🌟 Our Star Performers</h2>
        <p>
          A glimpse of students who demonstrated consistent effort, discipline,
          and excellence in academics.
        </p>
        <br></br>

        <div className="toppers-grid">
          <div className="topper-card">
            <img src="/images/topper1.jpeg" alt="Ananya R" />
            <h3>Karna B V</h3>
            <p>Class 10 • 97.76%</p>
          </div>

          <div className="topper-card">
            <img src="/images/topper2.jpeg" alt="Rahul S" />
            <h3>Maneesh M Gowda</h3>
            <p>Class 10 • 97.76%</p>
          </div>

          <div className="topper-card">
            <img src="/images/topper3.jpeg" alt="Megha K" />
            <h3>Hithesh P</h3>
            <p>Class 10 • 97.12%</p>
          </div>
        </div>
      </section>

      {/* RESULT SNAPSHOT */}
      <section className="about-section reveal-left">
        <h2>📈 Result Snapshot</h2>
        <p>
          Our exam-focused training ensures students perform confidently under
          real examination conditions.
        </p>

        <ul className="feature-list">
          <li>Majority of students scoring above 85%</li>
          <li>Consistent subject-wise toppers</li>
          <li>Strong foundation leading to long-term success</li>
          <li>Reduced exam stress through mock tests</li>
        </ul>
      </section>

      {/* TRUST & FEEDBACK */}
      <section className="about-section reveal-right">
        <div className="about-row">
          <div className="about-content">
            <h2>🤝 Trusted by Parents & Students</h2>
            <p>
              ACCESS is trusted for its disciplined environment, transparent
              academic tracking, and personalized student care.
            </p>

            <ul className="feature-list">
              <li>Regular parent-teacher interactions</li>
              <li>Individual performance feedback</li>
              <li>Focus on conceptual learning over rote methods</li>
              <li>Continuous motivation and mentoring</li>
              <li>Safe, structured, and supportive learning environment</li>
            </ul>
          </div>

          <img
            src="/images/parent.jpg"
            alt="Parent Trust"
            className="about-image"
          />
        </div>
      </section>

      {/* FOOTER */}
      <section className="about-footer reveal">
        <p>
          At ACCESS Coaching Institute, results are not just numbers — they
          reflect discipline, consistency, confidence, and a strong academic
          foundation.
        </p>
      </section>
    </div>
  );
}
